import { useEffect, useState } from "react";
import { ExternalLink, GitCommit, MonitorPlay, Rocket } from "lucide-react";
import api from "../lib/api";
import { fmtDateTime } from "../lib/format";

function shortSha(value) {
  return value ? String(value).slice(0, 10) : "—";
}

function eventLabel(kind) {
  if (kind === "release_candidate") return "Release candidate";
  if (kind === "preview") return "Preview";
  if (kind === "deployment") return "Deployment";
  return String(kind || "Build event").replace(/_/g, " ");
}

function EventIcon({ kind }) {
  if (kind === "preview") return <MonitorPlay size={14} />;
  if (kind === "deployment") return <Rocket size={14} />;
  return <GitCommit size={14} />;
}

export default function BuildLincHistory({ projectId, refreshToken = "" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setEvents([]);
      return undefined;
    }

    const load = async () => {
      setLoading(true);
      setNotice("");
      try {
        const { data } = await api.get(`/projects/${projectId}/buildlinc/history?limit=20`);
        if (!cancelled) setEvents(data?.events || []);
      } catch {
        if (!cancelled) setNotice("Release history is temporarily unavailable. Current BuildLinc state is unaffected.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projectId, refreshToken]);

  return (
    <article className="buildlinc-panel" style={{ marginTop: 10 }} data-testid="buildlinc-release-history">
      <header>
        <div><GitCommit size={16} /><span><b>Release history</b><small>Durable RC, preview and deployment evidence from the audit ledger.</small></span></div>
        <em>{events.length}</em>
      </header>
      <div className="buildlinc-file-list">
        {notice ? <p>{notice}</p> : null}
        {!notice && loading && !events.length ? <p>Loading release history…</p> : null}
        {!notice && !loading && !events.length ? <p>No BuildLinc release events recorded for this project yet.</p> : null}
        {events.map((event) => (
          <div key={event.ledger_event_id || `${event.kind}-${event.recorded_at}-${event.sha}`}>
            <span>
              <b style={{ display: "flex", alignItems: "center", gap: 6 }}><EventIcon kind={event.kind} /> {eventLabel(event.kind)}</b>
              <small>{fmtDateTime(event.recorded_at)} · {event.branch || event.repository || "project"}{event.smoke_status ? ` · ${event.smoke_status}` : ""}</small>
              {event.note ? <small>{event.note}</small> : null}
              {event.preview_url ? <small><a href={event.preview_url} target="_blank" rel="noreferrer">Open preview <ExternalLink size={10} style={{ verticalAlign: "middle" }} /></a></small> : null}
            </span>
            <em className="mono">{shortSha(event.sha)}</em>
          </div>
        ))}
      </div>
    </article>
  );
}
